class Carousel {
  private readonly $slides: HTMLElement;

  private readonly $controls: HTMLElement | null;
  private readonly $prev: HTMLElement | null;
  private readonly $next: HTMLElement | null;

  private readonly $indicators: HTMLElement | null;

  private readonly loop: boolean = false;
  private readonly autoScroll: boolean = false;

  private readonly autoScrollInterval: number = 8000;

  private intervalId: number | null = null;

  private _pageSize: number = 0;

  get pageSize(): number {
    if (this._pageSize) {
      return this._pageSize;
    }

    this._pageSize = this.fetchPageSize();

    return this._pageSize;
  }

  get cloneCount(): number {
    return this.loop && this.pageSize === 1 ? 1 : 0;
  }

  get pageCount(): number {
    const childCount = this.$slides.childElementCount - this.cloneCount * 2;
    return Math.ceil(childCount / this.pageSize);
  }

  get currentPage(): number {
    const { scrollLeft, clientWidth } = this.$slides;
    return Math.round(scrollLeft / clientWidth) - this.cloneCount;
  }

  constructor(private readonly $root: HTMLElement) {
    const $slides = $root.querySelector<HTMLElement>(".carousel__slides");
    if (!$slides) {
      throw new Error(
        "Carousel needs a .carousel__slides element inside the root element",
      );
    }

    this.$slides = $slides;

    this.$controls = $root.querySelector(".carousel__controls");
    this.$prev = $root.querySelector(".carousel__prev");
    this.$next = $root.querySelector(".carousel__next");
    this.$indicators = $root.querySelector(".carousel__indicators");

    this.loop = $root.classList.contains("carousel--loop");
    this.autoScroll = $root.classList.contains("carousel--auto-scroll");

    if (this.$slides.childElementCount > 1) {
      this.init();
    }
  }

  private init() {
    new ResizeObserver(() => this.handleResize()).observe(this.$root);

    this.$slides.addEventListener("scroll", () => this.handleScroll());

    this.$prev?.addEventListener("click", () => this.move(-1));
    this.$next?.addEventListener("click", () => this.move(1));

    if (this.autoScroll) {
      this.intervalId = window.setInterval(() => {
        this.move(1);
      }, this.autoScrollInterval);
    }

    this.reset();
  }

  private reset() {
    this.resetClones();

    this.$slides.scrollTo({
      left: this.cloneCount * this.$slides.clientWidth,
      behavior: "auto",
    });

    this.$controls?.classList.remove("hidden");

    if (this.pageCount >= 2) {
      this.resetIndicators();
    } else {
      this.$controls?.classList.add("hidden");
    }
  }

  private resetClones() {
    this.$slides
      .querySelectorAll<HTMLElement>(".carousel__slide--clone")
      .forEach(($clone) => {
        $clone.remove();
      });

    if (this.loop && this.pageSize === 1) {
      const $firstSlide = this.$slides.firstElementChild?.cloneNode(
        true,
      ) as HTMLElement;
      const $lastSlide = this.$slides.lastElementChild?.cloneNode(
        true,
      ) as HTMLElement;

      if ($firstSlide && $lastSlide) {
        $firstSlide.classList.add("carousel__slide--clone");
        $lastSlide.classList.add("carousel__slide--clone");

        this.$slides.appendChild($firstSlide);
        this.$slides.prepend($lastSlide);
      }
    }
  }

  private resetIndicators() {
    if (this.$indicators) {
      this.$indicators.innerHTML = "";
    } else {
      return;
    }

    for (let i = 0; i < this.pageCount; i++) {
      const $indicator = document.createElement("button");
      $indicator.addEventListener("click", () => {
        this.moveTo(i);
        if (this.intervalId) {
          clearInterval(this.intervalId);
        }
      });

      if (i === 0) {
        $indicator.classList.add("active");
      }
      this.$indicators.appendChild($indicator);
    }
  }

  private fetchPageSize() {
    const pageSize = getComputedStyle(this.$root).getPropertyValue(
      "--page-size",
    );

    return parseInt(pageSize, 10) || 1;
  }

  private didPageSizeChange(): boolean {
    const pageSize = this.fetchPageSize();

    return this._pageSize === 0 || pageSize !== this._pageSize;
  }

  private handleResize() {
    if (this.didPageSizeChange()) {
      this._pageSize = this.fetchPageSize();
      this.reset();
    }
  }

  private handleScroll() {
    this.$indicators
      ?.querySelectorAll<HTMLElement>("button")
      .forEach(($indicator) => {
        $indicator.classList.remove("active");
      });

    this.$indicators
      ?.querySelectorAll<HTMLElement>("button")
      .forEach(($indicator, i) => {
        if (i === this.currentPage) {
          $indicator.classList.add("active");
        }
      });
  }

  private move(dir: number) {
    this.moveTo(this.currentPage + dir);
  }

  private moveTo(index: number) {
    const { clientWidth } = this.$slides;

    if (index < 0) {
      index = this.pageCount + index;
      if (this.cloneCount > 0) {
        this.$slides.scrollTo({
          left: (this.pageCount + this.cloneCount) * clientWidth,
          behavior: "auto",
        });
      }
    } else if (index >= this.pageCount) {
      index = index - this.pageCount;
      if (this.cloneCount > 0) {
        this.$slides.scrollTo({
          left: 0,
          behavior: "auto",
        });
      }
    }

    this.$slides.scrollTo({
      left: (index + this.cloneCount) * clientWidth,
      behavior: "smooth",
    });
  }
}

function init(scope: ParentNode = document) {
  scope
    .querySelectorAll<HTMLElement>(".carousel")
    .forEach((el) => new Carousel(el));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init());
} else {
  init();
}

export {};
